import {
  useCancelCmpNewsletterScheduleMutation,
  usePreviewCmpNewsletterTestSendMutation,
  useScheduleCmpNewsletterMutation,
  useSendCmpNewsletterTestMutation,
} from "../../../graphql/types";
import {
  EMAIL_CHAMBER_LOADING_MESSAGE,
  EMAIL_OPERATION_ERROR_CONTEXT,
  emailSchedulePartsToUtcMilli,
  newsletterReturnToFormState,
} from "./emailBackendMappers";
import type { EmailFormState } from "./emailMockData";
import { UserFacingError } from "../../../utils/userFacingError";
import type {
  NewsletterTestSendPreview,
  NewsletterTestSendResult,
} from "./emailTestSend";

type UseNewsletterDeliveryActionsInput = {
  chamberId: string;
  timeZone?: string | null;
  /** The saved newsletter id for the email that EmailForm just saved. */
  getNewsletterId: (email: EmailFormState) => string | undefined;
  /** Best-effort refresh after a confirmed mutation. */
  afterMutation?: () => Promise<void>;
};

// Schedule, cancel-schedule and test-send actions shared by Create Email and
// Update Email. EmailForm saves the email before it calls any of these, so a
// new email has a saved id here. Test sends keep the server preview and
// confirmation token flow, and the backend keeps the test recipient routing.
export const useNewsletterDeliveryActions = ({
  chamberId,
  timeZone,
  getNewsletterId,
  afterMutation,
}: UseNewsletterDeliveryActionsInput) => {
  const [previewCmpNewsletterTestSend, { loading: previewingTestSend }] =
    usePreviewCmpNewsletterTestSendMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });
  const [sendCmpNewsletterTest, { loading: sendingTestEmail }] =
    useSendCmpNewsletterTestMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });
  const [scheduleCmpNewsletter, { loading: schedulingNewsletter }] =
    useScheduleCmpNewsletterMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });
  const [cancelCmpNewsletterSchedule, { loading: cancelingSchedule }] =
    useCancelCmpNewsletterScheduleMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });

  const requireNewsletterId = (email: EmailFormState): string => {
    const newsletterId = getNewsletterId(email);
    if (!chamberId) {
      throw new UserFacingError(EMAIL_CHAMBER_LOADING_MESSAGE);
    }
    if (!newsletterId) {
      throw new UserFacingError("Save the email first, then try again.");
    }
    return newsletterId;
  };
  const refreshAfterMutation = async () => {
    // The confirmed mutation result is authoritative. A refresh error must
    // not report a successful action as a failure or prompt a retry.
    await Promise.allSettled([afterMutation?.()]);
  };

  const previewTestSend = async (
    email: EmailFormState,
    recipientEmails: string[],
    clientRequestId: string
  ): Promise<NewsletterTestSendPreview> => {
    const newsletterId = requireNewsletterId(email);
    const result = await previewCmpNewsletterTestSend({
      variables: {
        input: {
          chamberId,
          newsletterId,
          recipientEmails,
          clientRequestId,
          note: email.reviewNote || undefined,
        },
      },
    });
    const preview = result.data?.previewCmpNewsletterTestSend;

    if (!preview) {
      throw new Error("The test email preview could not be loaded.");
    }

    return {
      normalizedRecipientEmails: [...preview.normalizedRecipientEmails],
      fromEmail: preview.fromEmail,
      replyToEmail: preview.replyToEmail,
      explicitRecipientsAllowed: preview.explicitRecipientsAllowed,
      blockingReason: preview.blockingReason,
      confirmationToken: preview.confirmationToken,
      confirmationExpiresAtUtcMilli: preview.confirmationExpiresAtUtcMilli,
    };
  };

  const sendReview = async (
    email: EmailFormState,
    recipientEmails: string[],
    clientRequestId: string,
    confirmationToken: string
  ): Promise<NewsletterTestSendResult & { email: EmailFormState }> => {
    const newsletterId = requireNewsletterId(email);
    const result = await sendCmpNewsletterTest({
      variables: {
        input: {
          chamberId,
          newsletterId,
          recipientEmails,
          clientRequestId,
          confirmationToken,
          note: email.reviewNote || undefined,
        },
      },
    });
    const sendResult = result.data?.sendCmpNewsletterTest;
    const savedEmail = sendResult?.newsletter;

    if (!sendResult?.newsletterSendQueuedSuccessfully || !savedEmail) {
      throw new Error("The test email could not be sent.");
    }

    const actualRecipientEmails = Array.from(
      new Set(
        sendResult.outboundEmails
          .map((outboundEmail) => outboundEmail.toEmail.trim().toLowerCase())
          .filter(Boolean)
      )
    );

    if (actualRecipientEmails.length === 0) {
      throw new UserFacingError(
        "We could not confirm who received the test email. Check the inbox you entered. If it did not arrive, select Send Test Email again."
      );
    }

    await refreshAfterMutation();

    return {
      email: newsletterReturnToFormState(savedEmail, timeZone || ""),
      actualRecipientEmails,
    };
  };

  const scheduleEmail = async (email: EmailFormState) => {
    const newsletterId = requireNewsletterId(email);
    const scheduledAtUtcMilli = emailSchedulePartsToUtcMilli(
      email.scheduledDate,
      email.scheduledTime,
      timeZone
    );

    if (!scheduledAtUtcMilli) {
      throw new UserFacingError("Choose a Scheduled Date and Scheduled Time.");
    }

    const result = await scheduleCmpNewsletter({
      variables: {
        input: {
          chamberId,
          newsletterId,
          scheduledAtUtcMilli,
          note: email.reviewNote || undefined,
        },
      },
    });
    const scheduleResult = result.data?.scheduleCmpNewsletter;
    const savedEmail = scheduleResult?.newsletter;

    if (scheduleResult?.newsletterSavedSuccessfully !== true || !savedEmail) {
      throw new Error("The email could not be scheduled.");
    }

    await refreshAfterMutation();

    return newsletterReturnToFormState(savedEmail, timeZone || "");
  };

  const cancelSchedule = async (email: EmailFormState) => {
    const newsletterId = requireNewsletterId(email);
    const result = await cancelCmpNewsletterSchedule({
      variables: {
        input: {
          chamberId,
          newsletterId,
          note: email.reviewNote || undefined,
        },
      },
    });
    const cancelResult = result.data?.cancelCmpNewsletterSchedule;
    const savedEmail = cancelResult?.newsletter;

    if (cancelResult?.newsletterSavedSuccessfully !== true || !savedEmail) {
      throw new Error("The scheduled email could not be canceled.");
    }

    await refreshAfterMutation();

    return newsletterReturnToFormState(savedEmail, timeZone || "");
  };

  return {
    previewTestSend,
    sendReview,
    scheduleEmail,
    cancelSchedule,
    isRunning:
      previewingTestSend ||
      sendingTestEmail ||
      schedulingNewsletter ||
      cancelingSchedule,
  };
};
